import React from 'react';
import { Award, ShieldCheck, CheckCircle, PhoneCall, Sparkles, MapPin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const HakimProfileBanner = ({ onConsultClick }) => {
  const { lang } = useLanguage();
  const { settings, cmsContent } = useStore();

  const hakimBio = lang === 'ur'
    ? (cmsContent?.hakimProfile?.bioUrdu || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی طبِ پاکستانی (قانون مفرد اعضاء) کے مستند اور کہنہ مشق نباض ہیں۔ آپ حضرت صابر ملتانیؒ کے سائنسی اصولوں اور علاج بالضد (تحریک، تسکین، تحلیل) کے تحت خالص ترین قدرتی اجزاء سے تیار کردہ نسخہ جات تجویز فرماتے ہیں۔')
    : (cmsContent?.hakimProfile?.bioEn || 'Hakim Maulana Syed Abdul Wahab Shah Shirazi is an authorized practitioner (FTJ) specializing in Tibb-e-Pakistani (Qanoon-e-Mufrad Aza). Remedies are prepared according to organ-tissue principles (Tahreek, Taskeen, Tahleel).');

  const qualificationBadge = lang === 'ur'
    ? (cmsContent?.hakimProfile?.qualificationUrdu || 'فاضل طب والجراحت (FTJ) • ماہر قانون مفرد اعضاء')
    : (cmsContent?.hakimProfile?.qualificationEn || 'FTJ Graduate • Tibb-e-Pakistani Specialist');

  const designationBadge = lang === 'ur'
    ? (cmsContent?.hakimProfile?.designationUrdu || 'طبی سرپرست دواخانہ طب پاکستانی')
    : (cmsContent?.hakimProfile?.designationEn || 'Medical Director, Dawakhana Tib-e-Pakistani');

  return (
    <section className="py-14 bg-gradient-to-r from-emerald-900 via-pansar-900 to-emerald-950 text-white relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-10 backdrop-blur-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Hakim Image / Avatar */}
            <div className="lg:col-span-4 text-center">
              <div className="relative inline-block">
                <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto rounded-3xl overflow-hidden border-4 border-amber-400/40 shadow-2xl bg-emerald-950">
                  <img
                    src={cmsContent?.hakimProfile?.photoUrl || "./images/hakim-shirazi.jpg"}
                    alt={lang === 'ur' ? settings.hakimNameUrdu : settings.hakimNameEn}
                    className="w-full h-full object-cover object-top filter brightness-95"
                  />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-slate-950 text-xs font-black font-urdu shadow-lg whitespace-nowrap">
                  {qualificationBadge}
                </div>
              </div>
            </div>

            {/* Content Info */}
            <div className="lg:col-span-8 space-y-4 text-center lg:text-start">
              
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-800/80 border border-emerald-700 text-amber-300 text-xs font-semibold font-urdu">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span>{designationBadge}</span>
              </div>

              <h2 className="text-2xl sm:text-4xl font-black font-title text-white tracking-tight">
                {lang === 'ur' ? settings.hakimNameUrdu : settings.hakimNameEn}
              </h2>

              <p className="text-amber-300 font-bold text-sm sm:text-base font-title">
                {lang === 'ur' 
                  ? `${settings.dawakhanaNameUrdu} • بانی و مہتمم ${settings.storeNameUrdu}`
                  : `${settings.dawakhanaNameEn} • Founder & Patron, ${settings.storeNameEn}`}
              </p>

              <p className="text-emerald-100 text-xs sm:text-sm font-urdu leading-relaxed max-w-2xl">
                {hakimBio}
              </p>

              {/* Consultation Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs font-urdu text-emerald-200">
                <div className="flex items-center gap-2 justify-center lg:justify-start">
                  <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    {lang === 'ur' 
                      ? (cmsContent?.hakimProfile?.feature1Urdu || 'نبض شناسی و مزاجی تشخیص')
                      : (cmsContent?.hakimProfile?.feature1En || 'Pulse Diagnostics & Mizaj Evaluation')}
                  </span>
                </div>
                <div className="flex items-center gap-2 justify-center lg:justify-start">
                  <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    {lang === 'ur' 
                      ? (cmsContent?.hakimProfile?.feature2Urdu || 'خالص نسخہ جات کی دستی تیاری')
                      : (cmsContent?.hakimProfile?.feature2En || 'Handcrafted Botanical Formulations')}
                  </span>
                </div>
                <div className="flex items-center gap-2 justify-center lg:justify-start">
                  <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    {lang === 'ur' 
                      ? 'مطب پر بالمشافہ معائنہ و تشخیص'
                      : 'In-Person Clinic Consultation & Diagnosis'}
                  </span>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-3 flex flex-wrap items-center justify-center lg:justify-start gap-4">
                <button
                  type="button"
                  onClick={onConsultClick}
                  className="px-8 py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs sm:text-sm transition-all shadow-xl flex items-center gap-2 font-urdu cursor-pointer transform hover:-translate-y-0.5"
                >
                  <MapPin className="w-4 h-4 text-slate-950" />
                  <span>
                    {lang === 'ur' 
                      ? 'دواخانہ تشریف لائیں • لوکیشن و پتہ'
                      : 'Visit Clinic • Location & Address'}
                  </span>
                </button>
              </div>

            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default HakimProfileBanner;
